import Axios from 'axios';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Store } from '../../Store';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { getError } from '../../utils';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../Api';

/**
 * EDIT A MEMBER
 * =============
 * One screen, three columns on a desktop and one column on a phone:
 *
 *   header   who this is, and the one Save button for the whole form
 *   left     their details, grouped the way a person thinks about them
 *   right    photo, password, and their profile-edit allowance + history
 *
 * Members may only fill in fields that are still EMPTY. Once a field holds a
 * value, changing it is done here — and every change made here counts against
 * that field's limit, exactly as the member's own fill did. So the chip beside
 * each field is how many changes that value has left in total, not a note about
 * somebody else's allowance. Every change is recorded as an office change, so
 * the history still explains every value.
 */

const SECTIONS = [
  {
    title: 'Who they are',
    icon: 'fa-user',
    fields: [
      { key: 'name', label: 'Full name', type: 'text', wide: false },
      { key: 'username', label: 'Username', type: 'text', locked: true, note: 'Identifies the account everywhere — cannot be changed here.' },
      { key: 'phone', label: 'Phone number', type: 'text' },
      { key: 'email', label: 'Email address', type: 'email' },
      { key: 'nid', label: 'NID / Passport / Birth certificate', type: 'number' },
      {
        key: 'gender',
        label: 'Gender',
        type: 'select',
        options: [
          { value: '', label: 'Select gender' },
          { value: 'male', label: 'Male' },
          { value: 'female', label: 'Female' },
        ],
      },
      { key: 'dob', label: 'Date of birth', type: 'date' },
      { key: 'address', label: 'Present address', type: 'text', wide: true },
      { key: 'fblink', label: 'Facebook URL', type: 'text', wide: true },
    ],
  },
  {
    title: 'Family contacts',
    icon: 'fa-users',
    fields: [
      { key: 'fatherphone', label: "Father's phone", type: 'text' },
      { key: 'motherphone', label: "Mother's phone", type: 'text' },
      { key: 'spousephone', label: "Spouse's phone", type: 'text' },
    ],
  },
  {
    title: 'Bank account',
    icon: 'fa-university',
    fields: [
      { key: 'bankname', label: 'Bank name', type: 'text' },
      { key: 'bankaccount', label: 'Account number', type: 'text' },
      { key: 'bankbranch', label: 'Branch', type: 'text' },
    ],
  },
  {
    title: 'Mobile banking',
    icon: 'fa-mobile',
    fields: [
      {
        key: 'mobilebankname',
        label: 'Service',
        type: 'select',
        options: [
          { value: '', label: 'Select service' },
          { value: 'bKash', label: 'bKash' },
          { value: 'Rocket', label: 'Rocket' },
          { value: 'Nagad', label: 'Nagad' },
        ],
      },
      { key: 'mobilebankaccount', label: 'Account number', type: 'text' },
    ],
  },
  {
    title: 'Nominee',
    icon: 'fa-heart',
    fields: [
      { key: 'benificiaryname', label: 'Nominee name', type: 'text' },
      { key: 'benificiarynid', label: 'Nominee NID', type: 'number' },
      { key: 'benificiaryrelation', label: 'Relationship', type: 'text' },
    ],
  },
  {
    title: 'Placement in the tree',
    icon: 'fa-sitemap',
    fields: [
      { key: 'reference', label: 'Reference username', type: 'text', unmanaged: true },
      { key: 'placement', label: 'Placement under', type: 'text', locked: true, note: 'Moving someone in the tree is done from the tree screen.' },
      { key: 'position', label: 'Position', type: 'text', locked: true },
    ],
  },
];

/** Everything the Save button sends. Placement and position are display-only. */
const SAVE_KEYS = SECTIONS.flatMap((s) =>
  s.fields.filter((f) => !f.locked).map((f) => f.key)
);

const ACTION_LABEL = {
  edit: 'Member changed it',
  admin: 'Office changed it',
  grant: 'Extra edits approved',
  reset: 'Counter reset',
};

const ACTION_STYLE = {
  edit: { bg: '#dbeafe', color: '#1d4ed8' },
  admin: { bg: '#e0e7ff', color: '#3730a3' },
  grant: { bg: '#dcfce7', color: '#166534' },
  reset: { bg: '#fef3c7', color: '#92400e' },
};

const asText = (v) => (v === null || v === undefined ? '' : String(v));

export default function EditUser() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const { id: userid } = useParams();
  const navigate = useNavigate();

  const auth = useMemo(
    () => ({ headers: { authorization: `Bearer ${userInfo?.token}` } }),
    [userInfo]
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [member, setMember] = useState(null);
  const [form, setForm] = useState({});
  const [original, setOriginal] = useState({});
  const [editInfo, setEditInfo] = useState({});
  const [photo, setPhoto] = useState('');
  const [sign, setSign] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await Axios.post(
        api + '/api/users/admin/getsetting',
        { _id: userid },
        auth
      );
      const next = {};
      for (const section of SECTIONS) {
        for (const field of section.fields) next[field.key] = asText(data[field.key]);
      }
      setMember(data);
      setForm(next);
      setOriginal(next);
      setPhoto(asText(data.profilePhoto));
      setSign(asText(data.sign));
      // /admin/getsetting ships the member's per-field allowance with the
      // profile, so the whole screen renders from a single request.
      setEditInfo(
        Object.fromEntries((data.profileEdit || []).map((f) => [f.key, f]))
      );
      setNewPassword(asText(data.plainPassword));
      setConfirmPassword(asText(data.plainPassword));
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userid, userInfo]);

  /* ------------------------------------------------------------------ save */

  const dirtyKeys = SAVE_KEYS.filter(
    (key) => asText(form[key]).trim() !== asText(original[key]).trim()
  );

  const save = async () => {
    if (!dirtyKeys.length) {
      toast.info('Nothing has changed.');
      return;
    }
    try {
      setSaving(true);
      const payload = { _id: userid };
      for (const key of dirtyKeys) payload[key] = form[key];
      await Axios.put(api + '/api/users/admin/savesetting', payload, auth);
      toast.success(
        `Saved ${dirtyKeys.length} change${dirtyKeys.length === 1 ? '' : 's'}.`
      );
      await fetchData();
      setOpenHistory('');
      setFieldHistory({});
      loadRecent();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setSaving(false);
    }
  };

  /* --------------------------------------------------------------- history */

  const [openHistory, setOpenHistory] = useState('');
  const [fieldHistory, setFieldHistory] = useState({});
  const [recent, setRecent] = useState({ rows: [], page: 1, pages: 1, count: 0 });
  const [recentOpen, setRecentOpen] = useState(false);
  const [recentField, setRecentField] = useState('');
  const [recentLoading, setRecentLoading] = useState(false);

  const loadRecent = async (opts = {}) => {
    const page = opts.page ?? 1;
    const field = opts.field ?? recentField;
    try {
      setRecentLoading(true);
      const params = new URLSearchParams({
        userId: userid,
        page: String(page),
        pageSize: '10',
      });
      if (field) params.set('field', field);
      const { data } = await Axios.get(
        api + `/api/users/admin/profile-edit/history?${params}`,
        auth
      );
      setRecent({
        rows: data.rows || [],
        page: data.page || 1,
        pages: data.pages || 1,
        count: data.count || 0,
      });
    } catch (error) {
      /* the log is informational — never block the edit screen over it */
    } finally {
      setRecentLoading(false);
    }
  };

  useEffect(() => {
    loadRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userid]);

  const toggleFieldHistory = async (key) => {
    if (openHistory === key) {
      setOpenHistory('');
      return;
    }
    setOpenHistory(key);
    if (fieldHistory[key]) return;
    try {
      const { data } = await Axios.get(
        api +
          `/api/users/admin/profile-edit/history?userId=${userid}&field=${key}&pageSize=20`,
        auth
      );
      setFieldHistory((prev) => ({ ...prev, [key]: data.rows || [] }));
    } catch (error) {
      toast.error(getError(error));
    }
  };

  /* ----------------------------------------------------------- allowance */

  const grant = async (key, extra) => {
    try {
      await Axios.post(
        api + '/api/users/admin/profile-edit/grant',
        { userId: userid, field: key, extra },
        auth
      );
      toast.success(
        extra > 0 ? 'Approved one more edit for them.' : 'Removed one granted edit.'
      );
      await fetchData();
    } catch (error) {
      toast.error(getError(error));
    }
  };

  /* ------------------------------------------------------------- password */

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');

  const changePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Type the new password twice.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('The two passwords do not match.');
      return;
    }
    try {
      await Axios.put(
        api + '/api/users/admin/changepassword',
        { _id: userid, password: newPassword },
        auth
      );
      toast.success('Password changed.');
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const resetPassword = async () => {
    if (
      !window.confirm(
        'Generate a new 6-digit password for this member? Their current password stops working immediately.'
      )
    )
      return;
    try {
      setResetLoading(true);
      const { data } = await Axios.post(
        api + '/api/users/admin/resetpassword',
        { _id: userid },
        auth
      );
      setGeneratedPassword(data.password);
      setNewPassword(data.password);
      setConfirmPassword(data.password);
      toast.success('New password generated.');
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setResetLoading(false);
    }
  };

  const copyPassword = () => {
    if (!generatedPassword) return;
    navigator.clipboard.writeText(generatedPassword).then(
      () => toast.success('Password copied.'),
      () => toast.error('Copy failed.')
    );
  };

  /* --------------------------------------------------------------- images */

  const upload = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSize = kind === 'profilePhoto' ? 266536 : 166536;
    if (file.type !== 'image/jpeg') {
      toast.error('Only JPG images are allowed.');
      return;
    }
    if (file.size > maxSize) {
      toast.error(`Max image size is ${kind === 'profilePhoto' ? 250 : 150} KB.`);
      return;
    }
    const body = new FormData();
    body.append('file', file);
    try {
      setUploading(true);
      const { data } = await Axios.post(api + '/api/upload/userphoto', body, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${userInfo.token}`,
        },
      });
      // Note: this is the MEMBER's image, never the signed-in admin's — the old
      // version wrote it into the admin's own stored profile by mistake.
      await Axios.put(
        api + '/api/users/admin/savephoto',
        { _id: userid, value: kind, link: data.secure_url },
        auth
      );
      if (kind === 'profilePhoto') setPhoto(data.secure_url);
      else setSign(data.secure_url);
      toast.success('Image uploaded.');
      loadRecent();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setUploading(false);
    }
  };

  /* ------------------------------------------------------------ rendering */

  /**
   * How many changes this field has left. The limit is shared: the member
   * spends one by filling a blank field, and every change made here spends one
   * too, so this chip is what stops a value moving forever.
   */
  const allowanceLabel = (info) => {
    if (!info) return null;
    const green = { bg: '#dcfce7', color: '#166534' };
    const red = { bg: '#fee2e2', color: '#991b1b' };
    const grey = { bg: '#e5e7eb', color: '#374151' };
    const amber = { bg: '#fef3c7', color: '#92400e' };
    switch (info.adminLockCode) {
      case 'system':
        return { text: 'Office only', ...grey };
      case 'off':
        return { text: 'Editing switched off', ...grey };
      case 'spent':
        return { text: `Limit reached · ${info.used} of ${info.allowed} used`, ...red };
      default:
        return info.unlimited
          ? { text: 'No limit', ...amber }
          : { text: `${info.remaining} of ${info.allowed} edits left`, ...green };
    }
  };

  const renderField = (field) => {
    const info = editInfo[field.key];
    const chip = allowanceLabel(info);
    // Out of edits means out of edits for staff too — raise the limit or allow
    // one more, rather than letting a save fail at the server.
    const isLockedHere = Boolean(field.locked)
      || Boolean(info && !field.unmanaged && !info.adminCanEdit);
    const dirty = !isLockedHere &&
      asText(form[field.key]).trim() !== asText(original[field.key]).trim();
    const rows = fieldHistory[field.key];
    const changeCount = info?.used ?? 0;

    return (
      <div
        key={field.key}
        className={field.wide ? 'eu-field eu-field-wide' : 'eu-field'}
      >
        <div className="eu-field-head">
          <label className="eu-label" htmlFor={`eu-${field.key}`}>
            {field.label}
            {dirty ? <span className="eu-dot" title="Not saved yet" /> : null}
          </label>
          {chip ? (
            <span
              className="eu-chip"
              style={{ background: chip.bg, color: chip.color }}
            >
              {chip.text}
            </span>
          ) : null}
        </div>

        {field.type === 'select' ? (
          <select
            id={`eu-${field.key}`}
            className="eu-input"
            value={form[field.key] ?? ''}
            disabled={isLockedHere}
            onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
          >
            {field.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            id={`eu-${field.key}`}
            type={field.type}
            className="eu-input"
            value={form[field.key] ?? ''}
            readOnly={isLockedHere}
            onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
          />
        )}

        <div className="eu-field-foot">
          {field.note ? <span className="eu-note">{field.note}</span> : null}
          {info && !field.unmanaged ? (
            <button
              type="button"
              className="eu-linkbtn"
              onClick={() => toggleFieldHistory(field.key)}
            >
              <i className="fa fa-history" aria-hidden="true" />{' '}
              {changeCount
                ? `Changed ${changeCount} time${changeCount === 1 ? '' : 's'}`
                : 'Never changed'}
              {openHistory === field.key ? ' ▲' : ' ▼'}
            </button>
          ) : null}
          {info && !info.adminCanEdit && !info.system && userInfo?.isAdmin ? (
            <button
              type="button"
              className="eu-linkbtn eu-linkbtn-strong"
              onClick={() => grant(field.key, 1)}
            >
              + Allow one more edit
            </button>
          ) : null}
        </div>

        {openHistory === field.key ? (
          <div className="eu-history">
            {!rows ? (
              <div className="eu-muted">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="eu-muted">This field has never been changed.</div>
            ) : (
              rows.map((row) => {
                const style = ACTION_STYLE[row.action] || ACTION_STYLE.edit;
                return (
                  <div key={row._id} className="eu-history-row">
                    <div className="eu-history-when">
                      {new Date(row.createdAt).toLocaleString()}
                    </div>
                    <div className="eu-history-change">
                      <span className="eu-old">{row.from || '—'}</span>
                      {' → '}
                      <span className="eu-new">{row.to || '—'}</span>
                    </div>
                    <div>
                      <span
                        className="eu-chip"
                        style={{ background: style.bg, color: style.color }}
                      >
                        {ACTION_LABEL[row.action] || row.action}
                      </span>{' '}
                      <span className="eu-muted">
                        {row.changedByName || '—'} · {row.source}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const limited = Object.values(editInfo).filter(
    (f) => !f.system && !f.unlimited
  );
  const outOfEdits = limited.filter((f) => !f.adminCanEdit);

  return (
    <div className="eu-page">
      <Helmet>
        <title>{member ? `Edit ${member.name}` : 'Edit User'}</title>
      </Helmet>
      <style>{CSS}</style>

      {/* Who this is, and the only Save button on the screen. */}
      <div className="eu-header">
        <img
          className="eu-avatar"
          src={photo || 'https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png'}
          alt=""
        />
        <div className="eu-who">
          <h4>{member?.name || (loading ? 'Loading…' : 'Member')}</h4>
          <div className="eu-muted">
            {member?.username ? `@${member.username}` : ''}
            {member?.phone ? ` · ${member.phone}` : ''}
          </div>
          <div className="eu-pills">
            {member?.isBlocked ? (
              <span className="eu-chip" style={{ background: '#fee2e2', color: '#991b1b' }}>
                Blocked
              </span>
            ) : null}
            {member?.isActiveId ? (
              <span className="eu-chip" style={{ background: '#dcfce7', color: '#166534' }}>
                Active ID
              </span>
            ) : (
              <span className="eu-chip" style={{ background: '#f3f4f6', color: '#374151' }}>
                Inactive ID
              </span>
            )}
        <Link to={`/admin/profile-edit-history?userId=${userid}`}>Full history</Link>

            {member?.isAgent ? (
              <span className="eu-chip" style={{ background: '#e0e7ff', color: '#3730a3' }}>
                Agent
              </span>
            ) : null}
            {outOfEdits.length ? (
              <span className="eu-chip" style={{ background: '#fee2e2', color: '#991b1b' }}>
                Limit reached on {outOfEdits.length} field
                {outOfEdits.length === 1 ? '' : 's'}
              </span>
            ) : null}
          </div>
        </div>
        <div className="eu-actions">
          
          <Button variant="light" onClick={() => navigate(-1)}>
            <i className="fa fa-arrow-left" aria-hidden="true" /> Back
          </Button>
          <Button variant="primary" onClick={save} disabled={saving || !dirtyKeys.length}>
            {saving ? (
              <Spinner animation="border" size="sm" />
            ) : dirtyKeys.length ? (
              `Save ${dirtyKeys.length} change${dirtyKeys.length === 1 ? '' : 's'}`
            ) : (
              'Saved'
            )}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="eu-card eu-muted">Loading this member…</div>
      ) : (
        <div className="eu-grid">
          <div className="eu-col-main">
            {SECTIONS.map((section) => (
              <div className="eu-card" key={section.title}>
                <div className="eu-card-head">
                  <i className={`fa ${section.icon}`} aria-hidden="true" />{' '}
                  {section.title}
                </div>
                <div className="eu-fields">{section.fields.map(renderField)}</div>
              </div>
            ))}
          </div>

          <div className="eu-col-side">
            {/* Photo & signature */}
            <div className="eu-card">
              <div className="eu-card-head">
                <i className="fa fa-picture-o" aria-hidden="true" /> Photo &amp;
                signature
              </div>
              <div className="eu-side-body">
                <img className="eu-photo" src={photo || undefined} alt="" />
                <label className="eu-file">
                  <span>Upload photo (JPG, max 250 KB)</span>
                  <input
                    type="file"
                    accept="image/jpeg"
                    onChange={(e) => upload(e, 'profilePhoto')}
                  />
                </label>
                {sign ? <img className="eu-sign" src={sign} alt="signature" /> : null}
                <label className="eu-file">
                  <span>Upload signature (JPG, max 150 KB)</span>
                  <input
                    type="file"
                    accept="image/jpeg"
                    onChange={(e) => upload(e, 'sign')}
                  />
                </label>
                {uploading ? <Spinner animation="border" size="sm" /> : null}
              </div>
            </div>

            {/* Their allowance at a glance */}
            <div className="eu-card">
              <div className="eu-card-head">
                <i className="fa fa-lock" aria-hidden="true" /> Their edit allowance
              </div>
              <div className="eu-side-body">
                {limited.length === 0 ? (
                  <div className="eu-muted">
                    No field is limited yet.{' '}
                    <Link to="/admin/profile-edit-limits">Set the limits</Link>.
                  </div>
                ) : (
                  limited.map((f) => (
                    <div className="eu-allow-row" key={f.key}>
                      <span>{f.label}</span>
                      <span
                        className="eu-chip"
                        style={
                          f.adminCanEdit
                            ? { background: '#dcfce7', color: '#166534' }
                            : { background: '#fee2e2', color: '#991b1b' }
                        }
                      >
                        {f.adminCanEdit
                          ? `${f.used} used · ${f.remaining} left`
                          : `${f.used} used · limit reached`}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Password */}
            <div className="eu-card">
              <div className="eu-card-head">
                <i className="fa fa-key" aria-hidden="true" /> Password
              </div>
              <div className="eu-side-body">
                {generatedPassword ? (
                  <div className="eu-alert">
                    <div>
                      <b>New password:</b>{' '}
                      <span className="eu-password">{generatedPassword}</span>
                      <div className="eu-muted">Give this to the member now.</div>
                    </div>
                    <Button size="sm" variant="outline-dark" onClick={copyPassword}>
                      Copy
                    </Button>
                  </div>
                ) : null}
                <Button
                  variant="warning"
                  className="w-100"
                  onClick={resetPassword}
                  disabled={resetLoading}
                >
                  {resetLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    'Generate a new 6-digit password'
                  )}
                </Button>
                <label className="eu-label">New password</label>
                <input
                  className="eu-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <label className="eu-label">Confirm password</label>
                <input
                  className="eu-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <Button variant="outline-success" className="w-100" onClick={changePassword}>
                  Set this password
                </Button>
              </div>
            </div>

            {/* Recent changes, folded away — it gets long. */}
            <div className="eu-card">
              <button
                type="button"
                className="eu-card-head eu-card-head-btn"
                onClick={() => setRecentOpen((v) => !v)}
              >
                <span>
                  <i className="fa fa-history" aria-hidden="true" /> Recent changes (
                  {recent.count})
                </span>
                <span>{recentOpen ? '▲' : '▼'}</span>
              </button>
              {recentOpen ? (
                <div className="eu-side-body">
                  <select
                    className="eu-input"
                    value={recentField}
                    onChange={(e) => {
                      setRecentField(e.target.value);
                      loadRecent({ page: 1, field: e.target.value });
                    }}
                  >
                    <option value="">All fields</option>
                    {Object.values(editInfo).map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label}
                      </option>
                    ))}
                  </select>

                  {recentLoading ? (
                    <div className="eu-muted">Loading…</div>
                  ) : recent.rows.length === 0 ? (
                    <div className="eu-muted">Nothing recorded yet.</div>
                  ) : (
                    recent.rows.map((row) => {
                      const style = ACTION_STYLE[row.action] || ACTION_STYLE.edit;
                      return (
                        <div key={row._id} className="eu-history-row">
                          <div className="eu-history-when">
                            {new Date(row.createdAt).toLocaleString()} ·{' '}
                            {row.label || row.field}
                          </div>
                          <div className="eu-history-change">
                            <span className="eu-old">{row.from || '—'}</span>
                            {' → '}
                            <span className="eu-new">{row.to || '—'}</span>
                          </div>
                          <span
                            className="eu-chip"
                            style={{ background: style.bg, color: style.color }}
                          >
                            {ACTION_LABEL[row.action] || row.action}
                          </span>
                        </div>
                      );
                    })
                  )}

                  <div className="eu-pager">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={recent.page <= 1}
                      onClick={() => loadRecent({ page: recent.page - 1 })}
                    >
                      ‹
                    </Button>
                    <span className="eu-muted">
                      Page {recent.page} of {recent.pages}
                    </span>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={recent.page >= recent.pages}
                      onClick={() => loadRecent({ page: recent.page + 1 })}
                    >
                      ›
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* On a phone the header scrolls away, so Save follows the thumb. */}
      {dirtyKeys.length ? (
        <div className="eu-sticky-save">
          <Button variant="primary" className="w-100" onClick={save} disabled={saving}>
            {saving ? (
              <Spinner animation="border" size="sm" />
            ) : (
              `Save ${dirtyKeys.length} change${dirtyKeys.length === 1 ? '' : 's'}`
            )}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Scoped to this screen. The admin shell puts a fixed-width sidebar on the
 * left, so the page is a right-hand column on a desktop and the full width
 * below 900px, where the sidebar stops being usable anyway.
 */
const CSS = `
.eu-page { width: 87%; float: right; padding: 16px 20px 90px; box-sizing: border-box; }
@media (max-width: 900px) { .eu-page { width: 100%; float: none; padding: 12px 12px 90px; } }

.eu-header { display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
  background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 14px 16px; }
.eu-avatar { width: 64px; height: 64px; border-radius: 50%; object-fit: cover; border: 1px solid #e5e7eb; }
.eu-who { flex: 1 1 220px; min-width: 0; }
.eu-who h4 { margin: 0; font-size: 20px; }
.eu-pills { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 6px; }
.eu-actions { display: flex; gap: 8px; flex-wrap: wrap; }
@media (max-width: 600px) { .eu-actions { width: 100%; } .eu-actions > * { flex: 1; } }

.eu-note-bar { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e3a8a;
  border-radius: 10px; padding: 9px 12px; font-size: 13px; margin: 12px 0; }

.eu-grid { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: 14px; align-items: start; }
@media (max-width: 1100px) { .eu-grid { grid-template-columns: 1fr; } }
.eu-col-main, .eu-col-side { display: flex; flex-direction: column; gap: 14px; min-width: 0; }

.eu-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; overflow: hidden; }
.eu-card-head { padding: 11px 14px; background: #f9fafb; border-bottom: 1px solid #e5e7eb;
  font-weight: 700; font-size: 13px; text-transform: uppercase; letter-spacing: .03em; color: #374151; }
.eu-card-head-btn { width: 100%; display: flex; justify-content: space-between; align-items: center;
  border: 0; border-bottom: 1px solid #e5e7eb; cursor: pointer; text-align: left; }

.eu-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; padding: 14px; }
@media (max-width: 700px) { .eu-fields { grid-template-columns: 1fr; } }
.eu-field { min-width: 0; }
.eu-field-wide { grid-column: 1 / -1; }
.eu-field-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 4px; }
.eu-label { font-size: 12px; font-weight: 600; color: #374151; margin: 6px 0 4px; display: block; }
.eu-dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: #2563eb; margin-left: 6px; }
.eu-input { width: 100%; border: 1px solid #d1d5db; border-radius: 9px; padding: 9px 11px;
  font-size: 14px; background: #fff; box-sizing: border-box; }
.eu-input:focus { outline: 2px solid #bfdbfe; border-color: #2563eb; }
.eu-input[readonly], .eu-input:disabled { background: #f3f4f6; color: #6b7280; }
.eu-field-foot { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-top: 5px; }
.eu-note { font-size: 11px; color: #6b7280; }
.eu-linkbtn { background: none; border: 0; padding: 0; font-size: 11.5px; color: #2563eb; cursor: pointer; }
.eu-linkbtn-strong { font-weight: 700; color: #166534; }

.eu-chip { border-radius: 999px; padding: 2px 9px; font-size: 11px; font-weight: 700; white-space: nowrap; }
.eu-muted { color: #6b7280; font-size: 12.5px; }

.eu-history { margin-top: 8px; border-top: 1px dashed #e5e7eb; padding-top: 8px;
  max-height: 240px; overflow-y: auto; }
.eu-history-row { padding: 6px 0; border-bottom: 1px solid #f3f4f6; }
.eu-history-when { font-size: 11px; color: #6b7280; }
.eu-history-change { font-size: 12.5px; overflow-wrap: anywhere; }
.eu-old { color: #991b1b; text-decoration: line-through; }
.eu-new { color: #166534; font-weight: 600; }

.eu-side-body { padding: 14px; display: flex; flex-direction: column; gap: 10px; }
.eu-photo { width: 100%; max-width: 160px; align-self: center; border-radius: 12px; border: 1px solid #e5e7eb; }
.eu-sign { width: 100%; max-width: 200px; align-self: center; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; }
.eu-file { font-size: 12px; color: #374151; display: flex; flex-direction: column; gap: 5px; }
.eu-file input { font-size: 12px; }
.eu-allow-row { display: flex; justify-content: space-between; align-items: center; gap: 8px;
  font-size: 12.5px; padding: 4px 0; border-bottom: 1px solid #f3f4f6; }
.eu-alert { background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px; padding: 10px;
  display: flex; justify-content: space-between; align-items: center; gap: 10px; }
.eu-password { font-size: 20px; letter-spacing: .18em; font-weight: 700; }
.eu-pager { display: flex; align-items: center; justify-content: center; gap: 10px; padding-top: 6px; }

.eu-sticky-save { position: sticky; bottom: 0; margin-top: 14px; padding: 10px 0;
  background: linear-gradient(to top, rgba(255,255,255,.98), rgba(255,255,255,.75)); }
@media (min-width: 1101px) { .eu-sticky-save { display: none; } }
`;
